// Catalog browsing state. Works in the browser with no session, and inside Revit through the C#
// proxy. Filtering is server-side (category, language, scope), so the store just holds the current
// query and the last page it fetched.
//
// "Scope" is which shelf is being listed: the public catalog, the caller's organization library, or
// their own families at any status. The server decides what each shelf contains — the client never
// filters by visibility itself, because a client-side filter over a server response that already
// contained other people's private families would not be a filter, it would be a cover-up.

import { transport } from "@/transport";
import type {
  FamilyPage,
  FamilySummary,
  FamilyTree,
  FavoriteResult,
  LoadedFamiliesResult,
  LoadedFamily,
  ParameterOption,
  PinnedLocation,
} from "./types";

/** Which shelf the grid is showing. Mirrors the API's `scope` query parameter. */
export type CatalogScope = "public" | "org" | "mine" | "favorites" | "history";

/** The three levels of the catalog tree. Section and category are derived from the family's
 *  BuiltInCategory and localized by key; subcategory is authored and shown verbatim. */
export type TreeLevel = "section" | "category" | "subcategory";

export interface FacetNode {
  key: string;
  count: number;
}

/**
 * How the grid is drawn. Not a cosmetic preference: a Revit side panel is narrow, a catalog can run
 * to hundreds of families, and the amount of chrome we put on a card is a decision we were making
 * for the user with no way for them to unmake it.
 */
export type CatalogView = "cozy" | "compact" | "list";

/** Ordering. "default" means the shelf's own — newest, or most recently marked, or most recently used. */
export type CatalogSort = "default" | "name" | "popular";

const VIEW_KEY = "apibim.fm.catalogView";
const SORT_KEY = "apibim.fm.catalogSort";

function restore<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
  } catch {
    // Private mode or storage disabled — the app works, the choice just does not persist.
    return fallback;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* best-effort */
  }
}

interface CatalogState {
  scope: CatalogScope;
  items: FamilySummary[];
  total: number;
  loading: boolean;
  error: string | null;
  /**
   * Where in the tree the user is standing. All three optional, so the path is a progressive
   * filter rather than a wall: at the root the whole shelf is still listed, and each step down
   * narrows it. A tree that showed nothing until you drilled two levels would be a regression from
   * a flat grid on a small catalog.
   */
  section: string | null;
  category: string | null;
  subcategory: string | null;

  /** The folders directly below the current position, with counts. */
  nodes: FacetNode[];
  /** What those nodes are, which decides how they are labelled and which level a click fills in. */
  nodeLevel: TreeLevel;

  /** How the grid is drawn, and how it is ordered. Both survive a restart. */
  view: CatalogView;
  sort: CatalogSort;
  /** Whether the filter panel is open. Filters live behind it rather than on screen always. */
  filtersOpen: boolean;

  /** Branches the user pinned, for the jump-back row. */
  pinned: PinnedLocation[];

  /**
   * What GetLoadedFamilies last reported for the active document. Empty outside Revit and before
   * the first load. Fetched once when the grid mounts, not per family and not per scroll — see
   * loadProjectState.
   */
  loadedFamilies: LoadedFamily[];

  language: string | null;
  /** "metric" or "imperial" — the unit the family's own dimensions are authored in. A fixed pair,
   *  unlike language, so no discovery step is needed to offer it as a filter. */
  unitSystem: string | null;
  /** Free-text query over name, description and tags. */
  search: string;
  /** Distinct authoring languages present, for the filter chips. Orthogonal to the tree. */
  languages: string[];

  /**
   * OmniClass branch the grid is narrowed to, and its title when we know one.
   *
   * A third axis, independent of both the shelf and the tree: the tree files a family by what Revit
   * calls it, OmniClass by what the industry calls it, and the two do not nest. Set by clicking a
   * code — "show me more like this" — which is the only way to reach it, because a picker over 2645
   * codes in the browsing UI would be a worse tree than the one already there.
   */
  omniClass: string | null;
  omniClassTitle: string | null;

  /**
   * The "has this parameter" filter — presence only, no value matching. At most one of the two is
   * set at a time, mirroring FamilyParameterDef's two language-neutral identities: a built-in
   * parameter's enum name, or a shared parameter's GUID.
   */
  parameterBuiltIn: string | null;
  parameterSharedGuid: string | null;
  /** The parameters currently offered by the picker, sourced from the catalog rather than a
   *  hardcoded list — see loadParameterOptions. */
  parameterOptions: ParameterOption[];
}

const state = $state<CatalogState>({
  scope: "public",
  items: [],
  total: 0,
  loading: false,
  error: null,
  section: null,
  category: null,
  subcategory: null,
  nodes: [],
  nodeLevel: "section",
  view: restore(VIEW_KEY, ["cozy", "compact", "list"] as const, "cozy"),
  sort: restore(SORT_KEY, ["default", "name", "popular"] as const, "default"),
  filtersOpen: false,
  pinned: [],
  loadedFamilies: [],
  language: null,
  unitSystem: null,
  search: "",
  languages: [],
  omniClass: null,
  omniClassTitle: null,
  parameterBuiltIn: null,
  parameterSharedGuid: null,
  parameterOptions: [],
});

async function load(): Promise<void> {
  state.loading = true;
  state.error = null;
  try {
    const page = await transport.invoke<FamilyPage>("BrowseFamilies", {
      section: state.section,
      category: state.category,
      subcategory: state.subcategory,
      language: state.language,
      unitSystem: state.unitSystem,
      search: state.search.trim() || null,
      scope: state.scope,
      sort: state.sort,
      omniClass: state.omniClass,
      parameterBuiltIn: state.parameterBuiltIn,
      parameterGuid: state.parameterSharedGuid,
      page: 1,
      pageSize: 60,
    });
    state.items = page.items;
    state.total = page.total;
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    state.items = [];
    state.total = 0;
  } finally {
    state.loading = false;
  }
}

/**
 * Loads the unfiltered catalog once to discover which authoring languages exist, so the language
 * chips reflect real data rather than a hardcoded list. Called on first mount.
 *
 * Categories no longer come from here — they are a level of the tree, counted by the server against
 * the caller's visibility. Deriving them from one page of results would have undercounted anything
 * past the first 200 families.
 */
async function loadFacets(): Promise<void> {
  try {
    const page = await transport.invoke<FamilyPage>("BrowseFamilies", { page: 1, pageSize: 200 });
    state.languages = [...new Set(page.items.map((f) => f.authoringLanguage).filter(Boolean) as string[])].sort();
  } catch {
    // A nicety; a failure here just leaves the language chips empty.
  }
}

/**
 * Loads the options for the "has this parameter" filter picker — sourced from what the catalog
 * actually has, the same reasoning as loadFacets, but a dedicated endpoint rather than a derived
 * page of results: unlike authoring language, a parameter is not on the summary card at all, so
 * there is nothing in a page of results to derive it from.
 */
async function loadParameterOptions(): Promise<void> {
  try {
    state.parameterOptions = await transport.invoke<ParameterOption[]>("GetParameterOptions");
  } catch {
    // A nicety; a failure here just leaves the parameter picker empty.
    state.parameterOptions = [];
  }
}

/** Fetches the folders directly below the current position, with their counts. */
async function loadTree(): Promise<void> {
  try {
    const tree = await transport.invoke<FamilyTree>("BrowseFamilyTree", {
      section: state.section,
      category: state.category,
      subcategory: state.subcategory,
      language: state.language,
      unitSystem: state.unitSystem,
      scope: state.scope,
      // The folder counts have to agree with the grid, or the tree offers folders that open onto
      // nothing — the same reason the language filter is sent here.
      omniClass: state.omniClass,
      parameterBuiltIn: state.parameterBuiltIn,
      parameterGuid: state.parameterSharedGuid,
    });
    state.nodes = tree.nodes;
    state.nodeLevel = tree.level;
  } catch {
    // The grid below is the substance; losing the folder tiles degrades navigation to filters and
    // search rather than breaking the screen.
    state.nodes = [];
  }
}

/** The pinned branches. Signed-out callers get an empty list rather than an error. */
async function loadPinned(): Promise<void> {
  try {
    state.pinned = (await transport.invoke<PinnedLocation[]>("GetPinnedLocations")) ?? [];
  } catch {
    // Unavailable signed out, and not worth a message: the row simply does not appear.
    state.pinned = [];
  }
}

/**
 * What is already loaded in the active document — Revit-only, and read exactly once when the grid
 * mounts (see CatalogView.vue). A live per-family check would mean re-running a Revit-side collector
 * on every scroll or filter change, which is the wrong cost for a fact that only changes when
 * someone loads or closes a family; the grid and the type picker cross-reference this snapshot
 * client-side instead (see projectState.ts).
 */
async function loadProjectState(): Promise<void> {
  if (!transport.caps.revit) return;
  try {
    const result = await transport.invoke<LoadedFamiliesResult>("GetLoadedFamilies");
    state.loadedFamilies = result.ok ? result.families : [];
  } catch {
    // Not worth surfacing as an error: the grid works fine without the badge, it just does not know.
    state.loadedFamilies = [];
  }
}

/** Whether the place currently on screen is pinned. The root is never pinnable — it is where the
 *  home crumb already goes. */
function isCurrentPinned(): boolean {
  if (!state.section && !state.category && !state.subcategory) return false;
  return state.pinned.some(
    (p) =>
      p.scope === state.scope &&
      (p.section ?? null) === state.section &&
      (p.category ?? null) === state.category &&
      (p.subcategory ?? null) === state.subcategory,
  );
}

/** Pins or unpins where we are. The server answers with the whole list, so no second request. */
async function togglePinned(): Promise<void> {
  if (!state.section && !state.category && !state.subcategory) return;

  const body = {
    scope: state.scope,
    section: state.section,
    category: state.category,
    subcategory: state.subcategory,
  };

  try {
    state.pinned = await transport.invoke<PinnedLocation[]>(
      isCurrentPinned() ? "UnpinLocation" : "PinLocation",
      body,
    );
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
  }
}

/** Jumps to a pinned branch, restoring the shelf it was pinned on along with the path. */
function goToPinned(pin: PinnedLocation): void {
  state.scope = pin.scope as CatalogScope;
  state.section = pin.section;
  state.category = pin.category;
  state.subcategory = pin.subcategory;
  reload();
}

/** Reloads both halves of the screen — the folders above and the grid below. */
function reload(): void {
  void loadTree();
  void load();
}

/** Descends into a folder. Which level it fills in depends on which level is on offer. */
function enter(key: string): void {
  if (state.nodeLevel === "section") {
    state.section = key;
    state.category = null;
    state.subcategory = null;
  } else if (state.nodeLevel === "category") {
    state.category = key;
    state.subcategory = null;
  } else {
    state.subcategory = key;
  }
  reload();
}

/**
 * Climbs back to a level, clearing everything below it — what a breadcrumb click means. Passing
 * "root" goes all the way up.
 */
function goTo(level: TreeLevel | "root"): void {
  if (level === "root") {
    state.section = null;
    state.category = null;
    state.subcategory = null;
  } else if (level === "section") {
    state.category = null;
    state.subcategory = null;
  } else if (level === "category") {
    state.subcategory = null;
  }
  reload();
}

/**
 * Switches shelves and reloads.
 *
 * The tree position is reset, unlike the language filter. Each shelf has its own tree — "Doors" in
 * your organization's library and "Doors" in the public catalog are different folders with
 * different counts — so carrying a path across would land the user in a folder that may not exist
 * on the shelf they just opened.
 */
function setScope(scope: CatalogScope) {
  if (state.scope === scope) return;
  state.scope = scope;
  state.section = null;
  state.category = null;
  state.subcategory = null;
  reload();
}

function setView(view: CatalogView) {
  state.view = view;
  persist(VIEW_KEY, view);
}

function setSort(sort: CatalogSort) {
  if (state.sort === sort) return;
  state.sort = sort;
  persist(SORT_KEY, sort);
  // Only the grid: ordering does not change which folders exist below the current position.
  void load();
}

function toggleFilters() {
  state.filtersOpen = !state.filtersOpen;
}

/** How many filters are narrowing the results right now — the badge on the Filters button. */
function activeFilterCount(): number {
  return (
    (state.language ? 1 : 0) +
    (state.unitSystem ? 1 : 0) +
    (state.search.trim() ? 1 : 0) +
    (state.omniClass ? 1 : 0) +
    (state.parameterBuiltIn || state.parameterSharedGuid ? 1 : 0)
  );
}

/**
 * Narrows to an OmniClass branch, or clears it when handed the branch already showing — so the same
 * click that filters also un-filters.
 *
 * The tree position is dropped. A code and a path are two answers to "where does this belong", and
 * keeping the old path would intersect them into a corner of the catalog nobody asked for: click
 * "Doors" under Architecture, then a curtain-wall code, and you would get nothing with no
 * indication why.
 */
function setOmniClass(code: string | null, title: string | null = null) {
  const next = code && code !== state.omniClass ? code : null;

  state.omniClass = next;
  state.omniClassTitle = next ? title : null;
  state.section = null;
  state.category = null;
  state.subcategory = null;
  reload();
}

/**
 * Drops the search text and every filter, but stays where it is in the tree. Clearing filters and
 * teleporting to the root are different intentions, and a control that did both would be the wrong
 * one half the time.
 */
function clearFilters() {
  state.search = "";
  state.language = null;
  state.unitSystem = null;
  state.omniClass = null;
  state.omniClassTitle = null;
  state.parameterBuiltIn = null;
  state.parameterSharedGuid = null;
  clearTimeout(searchTimer);
  reload();
}

function setLanguage(language: string | null) {
  state.language = state.language === language ? null : language;
  // The tree too: the counts on the folder tiles are filtered, so a language change that empties a
  // folder must remove it rather than leave a tile that opens onto nothing.
  reload();
}

function setUnitSystem(unitSystem: string | null) {
  state.unitSystem = state.unitSystem === unitSystem ? null : unitSystem;
  reload();
}

/** A stable id for a parameter option, for the picker's <select> — a kind prefix because a built-in
 *  name and a shared GUID are drawn from different spaces and could otherwise collide. */
function parameterOptionKey(option: Pick<ParameterOption, "kind" | "builtInParam" | "sharedGuid">): string {
  return option.kind === "builtin" ? `builtin:${option.builtInParam}` : `shared:${option.sharedGuid}`;
}

/** The key of whichever parameter filter is currently set, or "" for the picker's "any" option. */
function currentParameterKey(): string {
  if (state.parameterBuiltIn) return `builtin:${state.parameterBuiltIn}`;
  if (state.parameterSharedGuid) return `shared:${state.parameterSharedGuid}`;
  return "";
}

/** Sets or clears the "has this parameter" filter from the picker's selected key. */
function setParameter(key: string): void {
  if (!key) {
    state.parameterBuiltIn = null;
    state.parameterSharedGuid = null;
  } else {
    const separator = key.indexOf(":");
    const kind = key.slice(0, separator);
    const value = key.slice(separator + 1);
    state.parameterBuiltIn = kind === "builtin" ? value : null;
    state.parameterSharedGuid = kind === "shared" ? value : null;
  }
  reload();
}

// Search is debounced: the grid reloads a short beat after the user stops typing, not on every
// keystroke, so a query does not fire a request per character.
let searchTimer: ReturnType<typeof setTimeout> | undefined;
function setSearch(search: string) {
  state.search = search;
  clearTimeout(searchTimer);
  // Only the grid. The folder tiles deliberately ignore the search term: a search that emptied the
  // tree would take away the way back out of it, and the counts on the folders answer "how much is
  // in here", not "how much matches what I am typing".
  searchTimer = setTimeout(() => void load(), 300);
}

/**
 * Flips a bookmark and reflects it locally without a reload. The row is patched optimistically and
 * reverted if the server disagrees — a heart that waits for a round-trip before filling in feels
 * broken, and the failure case is rare enough to pay for with a flicker.
 *
 * On the favorites shelf an un-marked family no longer belongs, so it is dropped from the list;
 * anywhere else it simply loses its heart.
 */
async function toggleFavorite(slug: string): Promise<void> {
  const item = state.items.find((f) => f.slug === slug);
  if (!item) return;

  const next = !item.isFavorite;
  item.isFavorite = next;

  try {
    await transport.invoke<FavoriteResult>("SetFavorite", { slug, favorite: next });
    if (!next && state.scope === "favorites") {
      state.items = state.items.filter((f) => f.slug !== slug);
      state.total = Math.max(0, state.total - 1);
    }
  } catch (error) {
    item.isFavorite = !next;
    state.error = error instanceof Error ? error.message : String(error);
  }
}

/**
 * Reflects a bookmark that was flipped somewhere else — the detail dialog — so the card behind it
 * does not still show the old heart when the dialog closes.
 */
function markFavorite(slug: string, isFavorite: boolean): void {
  const item = state.items.find((f) => f.slug === slug);
  if (item) item.isFavorite = isFavorite;

  if (!isFavorite && state.scope === "favorites") {
    state.items = state.items.filter((f) => f.slug !== slug);
    state.total = Math.max(0, state.total - 1);
  }
}

/** Drops a family the detail dialog just deleted from whichever shelf is on screen — it is gone
 *  everywhere, not just on the shelf that happened to be open. */
function removeFamily(slug: string): void {
  if (!state.items.some((f) => f.slug === slug)) return;
  state.items = state.items.filter((f) => f.slug !== slug);
  state.total = Math.max(0, state.total - 1);
}

export const catalog = {
  // No readonly() equivalent for $state — the proxy is mutable. The guarantee that state only
  // changes through these actions is a convention here, not a type-checked one.
  get state() {
    return state;
  },
  load,
  reload,
  setView,
  setSort,
  toggleFilters,
  activeFilterCount,
  clearFilters,
  loadTree,
  enter,
  goTo,
  setScope,
  toggleFavorite,
  markFavorite,
  removeFamily,
  loadFacets,
  loadPinned,
  loadProjectState,
  isCurrentPinned,
  togglePinned,
  goToPinned,
  setLanguage,
  setUnitSystem,
  setOmniClass,
  setSearch,
  loadParameterOptions,
  parameterOptionKey,
  currentParameterKey,
  setParameter,
};
